import { StandardEvents } from '@shopify/events';

/**
 * LARCIN product page — the little that the blocks cannot do in HTML.
 *
 * Four jobs, and deliberately nothing else. The cart, the variant picker, the
 * gallery and the review app are all the theme's or the app's own, and none of
 * them is reimplemented here:
 *
 * 1. KEEPING THE BLOCKS IN STEP WITH THE CHOSEN VARIANT. Horizon fetches this
 *    section again whenever a variant is picked and hands the new markup to
 *    every listener on the same event. This takes that markup — no second
 *    request — and swaps in the pieces of it that belong to these blocks, so a
 *    price, an availability line or a delivery date is always the server's
 *    answer for the variant on screen rather than something recalculated here.
 *
 * 2. THE FEATURED REVIEW RAIL, which is arrows, dots, and reading the store's
 *    own Judge.me markup. The reviews themselves are the app's: this picks the
 *    ones that qualify out of the widget the app already rendered, and shows
 *    nothing at all when none do.
 *
 * 3. UGC VIDEOS: a silent few seconds on one card at a time so the row is
 *    not four stills, and a dialog for the film a customer actually taps.
 *    Only the card whose turn it is holds a video element.
 *
 * 4. Nothing for "Layers well with": each card carries the theme's own product
 *    form, so adding one goes through exactly the code the collection cards
 *    use, cart drawer included.
 */

const SYNC_ATTRIBUTE = 'data-larcin-sync';

/* ------------------------------------------------------------------ Variants
   The event carries a promise that resolves with the section HTML Horizon has
   just fetched. Everything below reads that same response. */
async function syncToChosenVariant(event) {
  try {
    const { detail } = await event.promise;
    if (!detail?.html) return;

    // Horizon hands its listeners the document it has already parsed; older
    // paths hand over the markup as a string. Both are read the same way.
    const source =
      typeof detail.html === 'string' ? new DOMParser().parseFromString(detail.html, 'text/html') : detail.html;

    for (const current of document.querySelectorAll(`[${SYNC_ATTRIBUTE}]`)) {
      const key = current.getAttribute(SYNC_ATTRIBUTE);
      const next = source.querySelector(`[${SYNC_ATTRIBUTE}="${CSS.escape(key)}"]`);
      if (!next) continue;

      // Anything inside the buy form is the theme’s: it morphs the button with
      // the same server response this is reading, price label included. Taking
      // a second run at it would only race with that. The rest is plain markup
      // nobody else touches, so replacing it is enough.
      if (current.closest('product-form-component')) continue;

      current.replaceWith(next.cloneNode(true));
    }
  } catch {
    /* A cancelled variant change leaves the page as it was. */
  }
}

function watchVariantChanges() {
  // Capture, not bubble: the picker dispatches the event on itself, and a
  // listener on the document is reached in the capture phase either way.
  document.addEventListener(StandardEvents.productSelect, syncToChosenVariant, { capture: true });
}

/* ------------------------------------------------------------ The clock
   One interval for the whole page, re-querying the DOM each tick. The
   delivery block is replaced wholesale when a variant changes, so anything
   holding a reference to that element would be counting down a node that
   is no longer on the page. */
function startCountdowns() {
  const pad = (value) => String(value).padStart(2, '0');

  const tick = () => {
    const clocks = document.querySelectorAll('[data-larcin-deadline]');
    if (!clocks.length) return;

    const now = Date.now() / 1000;

    for (const clock of clocks) {
      const deadline = Number(clock.getAttribute('data-larcin-deadline'));
      if (!Number.isFinite(deadline)) continue;

      const left = Math.max(0, Math.floor(deadline - now));
      const hours = Math.floor(left / 3600);
      const minutes = Math.floor((left % 3600) / 60);
      const seconds = left % 60;

      const sep = clock.getAttribute('data-larcin-format') === 'compact' ? ':' : ' : ';
      const next = `${pad(hours)}${sep}${pad(minutes)}${sep}${pad(seconds)}`;
      if (clock.textContent !== next) clock.textContent = next;
    }
  };

  tick();
  setInterval(tick, 1000);
}

/* ------------------------------------------------------------- Review rail
   A teaser on the page, the whole review on request. The card shows a line of
   the review, which is enough to be read as praise and not so much that the
   product page turns into a wall of other people's writing; a tap opens the
   review in full, with its photograph, its date and any reply. */

/**
 * The first usable URL out of a Judge.me image, which lazy-loads through
 * data-src and only sometimes has a src at all.
 * @param {Element | null} image
 */
function judgeMeImage(image) {
  if (!image) return '';
  const link = image.closest('a');
  return (
    link?.getAttribute('href') ||
    image.getAttribute('data-src') ||
    image.getAttribute('src') ||
    ''
  );
}

/**
 * The reviews this block is allowed to show, read out of the markup Judge.me
 * has already rendered for this product.
 *
 * @param {Element} block
 */
function eligibleReviews(block) {
  const source = block.querySelector('[data-larcin-review-source]');
  if (!(source instanceof HTMLTemplateElement)) return [];

  const minimum = Number(block.dataset.larcinReviewScore || 5);
  const verifiedOnly = block.dataset.larcinReviewVerified !== 'false';
  const limit = Number(block.dataset.larcinReviewLimit || 6);

  // The template's own content is inert: nothing in it loads or runs.
  const holder = source.content;

  const found = [];

  for (const review of holder.querySelectorAll('.jdgm-rev')) {
    const score = Number(review.querySelector('.jdgm-rev__rating')?.getAttribute('data-score') || 0);
    const verified = review.getAttribute('data-verified-buyer') === 'true';
    if (score < minimum) continue;
    if (verifiedOnly && !verified) continue;

    const body = review.querySelector('.jdgm-rev__body')?.textContent?.trim();
    if (!body) continue;

    const name = review.querySelector('.jdgm-rev__author')?.textContent?.trim() || '';
    const picture = judgeMeImage(review.querySelector('.jdgm-rev__pics img'));
    const face = judgeMeImage(review.querySelector('.jdgm-rev__icon img'));
    const reply = review.querySelector('.jdgm-rev__reply')?.textContent?.replace(/\s+/g, ' ').trim() || '';
    const stamp = review.querySelector('time.jdgm-rev__timestamp')?.getAttribute('datetime') || '';

    found.push({
      name,
      body,
      title: review.querySelector('.jdgm-rev__title')?.textContent?.trim() || '',
      initial: (name[0] || '★').toUpperCase(),
      // The card's round image is the review's own photograph when it has one,
      // then the reviewer's avatar; the dialog shows the photograph only.
      thumb: picture || face,
      picture,
      reply,
      date: stamp ? new Date(stamp) : null,
      verified,
      score,
    });

    if (found.length >= limit) break;
  }

  return found;
}

/**
 * The curated reviews from the block's settings, shaped like the ones above.
 *
 * @param {Element} block
 */
function curatedReviews(block) {
  const source = block.querySelector('[data-larcin-review-curated-list]');
  if (!source) return [];

  let list = [];
  try {
    list = JSON.parse(source.textContent || '[]');
  } catch (error) {
    return [];
  }

  return list
    .filter((review) => review && review.body)
    .map((review) => ({
      name: review.name || '',
      body: review.body,
      title: '',
      initial: ((review.name || '')[0] || '★').toUpperCase(),
      thumb: review.image || '',
      picture: '',
      reply: '',
      date: null,
      verified: Boolean(review.verified),
      score: Math.min(Math.max(Number(review.score) || 5, 1), 5),
    }));
}

/**
 * Splits a review into its first sentence and the rest, so the opening line
 * can be marked in the dialog the way it was on the card.
 * @param {string} text
 */
function firstSentence(text) {
  const match = text.match(/^(.+?[.!?…]+)(\s+|$)([\s\S]*)$/);
  if (!match || match[1].length > 220) return [text, ''];
  return [match[1], match[3]];
}

/** The dialog a review opens in. One for the page, reused. */
function reviewTheatre() {
  let dialog = document.querySelector('dialog.larcin-review-theatre');
  if (dialog instanceof HTMLDialogElement) return dialog;

  dialog = document.createElement('dialog');
  dialog.className = 'larcin-review-theatre';
  dialog.setAttribute('aria-label', 'Customer review');
  dialog.innerHTML =
    '<div class="larcin-review-theatre__frame">' +
    '<figure class="larcin-review-theatre__media" hidden><img alt=""></figure>' +
    '<div class="larcin-review-theatre__content">' +
    '<div class="larcin-review-theatre__head">' +
    '<strong class="larcin-review-theatre__name"></strong>' +
    '<span class="larcin-review-theatre__verified" hidden></span>' +
    '</div>' +
    '<div class="larcin-review-theatre__meta">' +
    '<span class="larcin-review-theatre__stars" aria-hidden="true"></span>' +
    '<time class="larcin-review-theatre__date"></time>' +
    '</div>' +
    '<p class="larcin-review-theatre__title" hidden></p>' +
    '<p class="larcin-review-theatre__body"></p>' +
    '<div class="larcin-review-theatre__reply" hidden>' +
    '<span class="larcin-review-theatre__reply-label"></span>' +
    '<p class="larcin-review-theatre__reply-text"></p>' +
    '</div>' +
    '</div>' +
    '</div>' +
    '<button type="button" class="larcin-review-theatre__close" data-larcin-review-close aria-label="Close">&times;</button>';

  const close = () => dialog instanceof HTMLDialogElement && dialog.close();

  dialog.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;
    // The backdrop is the dialog element itself: a click that lands on it
    // rather than inside the frame or on the button is a click outside.
    if (event.target === dialog || event.target.closest('[data-larcin-review-close]')) close();
  });

  document.body.append(dialog);
  return dialog;
}

/**
 * @param {ReturnType<typeof eligibleReviews>[number]} review
 * @param {Element} block
 */
function openReview(review, block) {
  const dialog = reviewTheatre();
  const pick = (selector) => dialog.querySelector(selector);
  const settings = block.dataset;

  // Carry the block's own colours and sizes onto the dialog, which lives at
  // the end of the body and so cannot inherit them.
  const section = block.closest('.larcin-review-section') || block;
  const style = section.getAttribute('style') || '';
  dialog.setAttribute('style', style);

  const media = pick('.larcin-review-theatre__media');
  const image = media?.querySelector('img');
  const hasPicture = Boolean(review.picture);
  if (media && image) {
    media.hidden = !hasPicture;
    if (hasPicture) image.src = review.picture;
    else image.removeAttribute('src');
  }
  dialog.classList.toggle('has-media', hasPicture);

  pick('.larcin-review-theatre__name').textContent = review.name;

  const verified = pick('.larcin-review-theatre__verified');
  verified.hidden = !review.verified;
  verified.textContent = settings.larcinReviewModalBadge || 'Verified';

  pick('.larcin-review-theatre__stars').textContent = '★★★★★'.slice(0, review.score);

  const date = pick('.larcin-review-theatre__date');
  const showDate = settings.larcinReviewShowDate !== 'false' && review.date && !Number.isNaN(review.date.getTime());
  date.hidden = !showDate;
  if (showDate) {
    date.dateTime = review.date.toISOString();
    date.textContent = new Intl.DateTimeFormat(undefined, { day: 'numeric', month: 'short', year: 'numeric' }).format(
      review.date
    );
  }

  const title = pick('.larcin-review-theatre__title');
  title.hidden = !review.title;
  title.textContent = review.title;

  // Written node by node, never as markup: a review is a customer's words and
  // must not be able to bring its own HTML into the page.
  const body = pick('.larcin-review-theatre__body');
  body.replaceChildren();
  if (settings.larcinReviewHighlight !== 'false') {
    const [opening, rest] = firstSentence(review.body);
    const mark = document.createElement('mark');
    mark.textContent = opening;
    body.append(mark);
    if (rest) body.append(document.createTextNode(' ' + rest));
  } else {
    body.textContent = review.body;
  }

  const reply = pick('.larcin-review-theatre__reply');
  reply.hidden = !review.reply;
  if (review.reply) {
    pick('.larcin-review-theatre__reply-label').textContent = settings.larcinReviewReplyLabel || 'Larcin replied:';
    pick('.larcin-review-theatre__reply-text').textContent = review.reply;
  }

  dialog.showModal();
}

/** @param {Element} block */
function buildReviewRail(block) {
  const rail = block.querySelector('[data-larcin-review-rail]');
  const dots = block.querySelector('[data-larcin-review-dots]');
  if (!rail) return;

  const reviews = eligibleReviews(block);

  // The merchant's curated reviews, typed in on the block. By default they
  // follow a product's own reviews when it has fewer than two that qualify, so
  // one review never stands alone in a rail that cannot scroll.
  const curated = curatedReviews(block);
  const curatedMode = block.dataset.larcinReviewCurated || 'empty';
  const limit = Number(block.dataset.larcinReviewLimit || 6);
  if (curatedMode === 'always' || (curatedMode === 'empty' && reviews.length < 2)) {
    reviews.push(...curated.slice(0, Math.max(limit - reviews.length, 0)));
  }

  // Nothing to show at all: the block stays hidden.
  if (!reviews.length) {
    block.hidden = true;
    return;
  }

  const stars = '★★★★★';
  const verifiedLabel = block.dataset.larcinReviewBadge || 'Verified Customer';
  const readLabel = block.dataset.larcinReviewReadLabel || 'Read the full review from';

  // Each card is a button, because the whole of it opens the review. Only
  // phrasing content may live inside a button, so the parts are spans.
  rail.innerHTML = reviews
    .map((review, index) => {
      const badge = review.verified ? '<span class="larcin-verified-badge">' + verifiedLabel + '</span>' : '';

      return (
        '<button type="button" class="larcin-review-card" data-larcin-review-index="' + index + '">' +
        '<span class="larcin-review-photo" aria-hidden="true"></span>' +
        '<span class="larcin-review-body">' +
        '<span class="larcin-review-head">' +
        '<span class="larcin-review-name"></span>' +
        '<span class="larcin-review-stars" aria-hidden="true">' + stars.slice(0, review.score) + '</span>' +
        badge +
        '</span>' +
        '<span class="larcin-review-copy"></span>' +
        '</span>' +
        '</button>'
      );
    })
    .join('');

  rail.querySelectorAll('.larcin-review-card').forEach((card, index) => {
    const review = reviews[index];
    const photo = card.querySelector('.larcin-review-photo');
    if (review.thumb) {
      const img = document.createElement('img');
      img.src = review.thumb;
      img.alt = '';
      img.loading = 'lazy';
      img.width = 96;
      img.height = 96;
      photo.append(img);
    } else {
      photo.textContent = review.initial;
    }
    card.querySelector('.larcin-review-name').textContent = review.name;
    card.querySelector('.larcin-review-copy').textContent = '“' + review.body + '”';
    card.setAttribute('aria-label', readLabel + ' ' + (review.name || 'a customer'));
    card.addEventListener('click', () => openReview(review, block));
  });

  block.hidden = false;

  if (dots) {
    dots.innerHTML = reviews
      .map((_, index) => '<li class="larcin-review-dot' + (index === 0 ? ' is-active' : '') + '"></li>')
      .join('');
  }

  // One card's width plus the gap between cards is the rail's own width,
  // because the rail's side padding equals half that gap on each side.
  const step = () => rail.clientWidth;

  const arrows = block.querySelectorAll('[data-larcin-review-arrow]');
  for (const arrow of arrows) {
    arrow.hidden = reviews.length < 2;
    arrow.addEventListener('click', () => {
      const direction = arrow.getAttribute('data-larcin-review-arrow') === 'next' ? 1 : -1;
      rail.scrollBy({ left: direction * step(), behavior: 'smooth' });
    });
  }

  if (dots && reviews.length > 1) {
    rail.addEventListener(
      'scroll',
      () => {
        const index = Math.round(rail.scrollLeft / Math.max(step(), 1));
        dots.querySelectorAll('.larcin-review-dot').forEach((dot, i) => {
          dot.classList.toggle('is-active', i === index);
        });
      },
      { passive: true }
    );
  }
}

/* --------------------------------------------------------------------- UGC
   Two behaviours, and neither of them downloads four films up front.

   IDLE: one card at a time plays a few silent seconds and hands over to the
   next, so the row reads as alive rather than as four stills. Only the card
   whose turn it is holds a video element; the rest are posters. The cycle
   runs only while the row is on screen and never for a visitor who has
   asked for reduced motion.

   TAPPED: the film opens in a dialog at full height with its own controls
   and its sound, because a card in a four-across row is no place to watch
   anything. */

const PREVIEW_SECONDS = 5;

/** @param {HTMLElement} trigger */
function previewVideoFor(trigger) {
  const existing = trigger.querySelector('video');
  if (existing instanceof HTMLVideoElement) return existing;

  const source = trigger.getAttribute('data-larcin-ugc-play');
  if (!source) return null;

  const video = document.createElement('video');
  video.src = source;
  video.muted = true;
  video.defaultMuted = true;
  video.loop = true;
  video.playsInline = true;
  video.preload = 'metadata';
  video.setAttribute('aria-hidden', 'true');
  video.tabIndex = -1;
  trigger.append(video);
  return video;
}

/** @param {Element} rail */
function cyclePreviews(rail) {
  const cards = [...rail.querySelectorAll('[data-larcin-ugc-play]')].filter(
    (card) => card instanceof HTMLElement
  );
  if (cards.length < 1) return;

  const seconds = Number(rail.getAttribute('data-larcin-ugc-seconds')) || PREVIEW_SECONDS;
  const calm = window.matchMedia('(prefers-reduced-motion: reduce)');

  let index = -1;
  let timer = 0;
  let onScreen = false;

  const stopAll = () => {
    for (const card of cards) {
      card.classList.remove('is-previewing');
      const video = card.querySelector('video');
      if (video instanceof HTMLVideoElement) {
        video.pause();
        video.currentTime = 0;
      }
    }
  };

  const step = () => {
    clearTimeout(timer);
    if (!onScreen || calm.matches) return;

    stopAll();
    index = (index + 1) % cards.length;

    const card = cards[index];
    const video = previewVideoFor(card);
    if (video) {
      card.classList.add('is-previewing');
      // A refused play is not an error worth surfacing: the poster is still
      // there and the next card gets its turn on schedule.
      video.play().catch(() => card.classList.remove('is-previewing'));
    }

    timer = setTimeout(step, seconds * 1000);
  };

  const watcher = new IntersectionObserver(
    (entries) => {
      onScreen = entries.some((entry) => entry.isIntersecting);
      if (onScreen) step();
      else {
        clearTimeout(timer);
        stopAll();
      }
    },
    { threshold: 0.25 }
  );
  watcher.observe(rail);

  calm.addEventListener('change', () => {
    if (calm.matches) {
      clearTimeout(timer);
      stopAll();
    } else step();
  });

  // A page that loads in a hidden tab gets a stale answer from the observer,
  // and the observer will not fire again on its own because nothing moved.
  // Coming back to the tab therefore measures the rail rather than trusting
  // the flag, or the row would sit still until the first scroll.
  const looksOnScreen = () => {
    const box = rail.getBoundingClientRect();
    return box.bottom > 0 && box.top < (window.innerHeight || 0);
  };

  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      clearTimeout(timer);
      stopAll();
      return;
    }
    onScreen = onScreen || looksOnScreen();
    if (onScreen) step();
  });
}

/** The dialog the tapped film opens in. One for the page, reused. */
function ugcTheatre() {
  let dialog = document.querySelector('dialog.larcin-ugc-theatre');
  if (dialog instanceof HTMLDialogElement) return dialog;

  dialog = document.createElement('dialog');
  dialog.className = 'larcin-ugc-theatre';
  dialog.innerHTML =
    '<button type="button" class="larcin-ugc-theatre__close" data-larcin-ugc-close aria-label="Close">&times;</button>' +
    '<video class="larcin-ugc-theatre__video" controls playsinline></video>';

  // Stopping the film is done on every path that closes the dialog rather
  // than only in the close handler, because that event is queued and has been
  // seen not to arrive. A film left with its source attached keeps streaming
  // behind the page.
  const stopFilm = () => {
    const video = dialog instanceof Element ? dialog.querySelector('video') : null;
    if (!(video instanceof HTMLVideoElement)) return;
    video.pause();
    video.removeAttribute('src');
    video.load();
  };

  const close = () => {
    stopFilm();
    if (dialog instanceof HTMLDialogElement) dialog.close();
  };

  dialog.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;
    // The backdrop is the dialog itself: a click that lands on the element
    // rather than on the film or the button is a click outside it.
    if (event.target === dialog || event.target.closest('[data-larcin-ugc-close]')) close();
  });

  // Escape does not come through the helper above, so both of its events stop
  // the film too. Running twice is harmless.
  dialog.addEventListener('cancel', stopFilm);
  dialog.addEventListener('close', stopFilm);

  document.body.append(dialog);
  return dialog;
}

function watchUgc() {
  document.addEventListener('click', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const trigger = target.closest('[data-larcin-ugc-play]');
    if (!(trigger instanceof HTMLElement)) return;

    const source = trigger.getAttribute('data-larcin-ugc-play');
    if (!source) return;

    const dialog = ugcTheatre();
    const video = dialog.querySelector('video');
    if (!(video instanceof HTMLVideoElement)) return;

    video.src = source;
    video.setAttribute('aria-label', trigger.getAttribute('data-larcin-ugc-label') || 'Customer video');
    dialog.showModal();
    video.play().catch(() => {
      /* A browser that will not autoplay with sound still shows the controls. */
    });
  });

  document.querySelectorAll('.larcin-ugc-rail').forEach(cyclePreviews);
}
/* ------------------------------------------------------------------- Start */
function start() {
  document.querySelectorAll('[data-larcin-reviews]').forEach(buildReviewRail);
}

if (!window.__larcinPdpStarted) {
  window.__larcinPdpStarted = true;
  watchVariantChanges();
  startCountdowns();
  watchUgc();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  // The rail is rebuilt after a variant change swaps its markup back in.
  document.addEventListener(StandardEvents.productSelect, () => {
    setTimeout(start, 120);
  });
}
